import React, { useState, useEffect, useCallback } from 'react';
import { App } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import apiClient from '../api/client';

// Every settings card reads and writes the same document, so the state and the
// requests live here — one GET on mount, one PUT per Save, exactly as before.
// The cards are then pure layout and can be kept small.

export interface ProxyTargetConfig {
  url: string;
  expectedContent: string;
}

export const useSettings = () => {
  const { message, modal } = App.useApp();
  const [checkInterval, setCheckInterval] = useState<number>(60);
  const [ticketsEnabled, setTicketsEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [togglingTickets, setTogglingTickets] = useState(false);

  // Proxy checker settings
  const [proxyMaxLatencyMs, setProxyMaxLatencyMs] = useState<number>(30000);
  const [proxyTimeoutMs, setProxyTimeoutMs] = useState<number>(30000);

  // Mobile-gateway settings (separate cadence + thresholds for modem proxies)
  const [checkIntervalMobile, setCheckIntervalMobile] = useState<number>(180);
  const [proxyMaxLatencyMsMobile, setProxyMaxLatencyMsMobile] = useState<number>(3000);
  const [proxyTimeoutMsMobile, setProxyTimeoutMsMobile] = useState<number>(20000);
  const [savingMobile, setSavingMobile] = useState(false);
  const [proxyTargetConfigs, setProxyTargetConfigs] = useState<ProxyTargetConfig[]>([
    { url: 'https://www.google.com', expectedContent: '<title>Google</title>' },
    { url: 'https://www.baidu.com', expectedContent: 'baidu.com' },
    { url: 'http://detectportal.firefox.com/success.txt', expectedContent: 'success' },
  ]);
  const [savingProxy, setSavingProxy] = useState(false);

  // Concurrency settings
  const [maxConcurrentChecks, setMaxConcurrentChecks] = useState<number>(50);
  const [maxChecksPerServer, setMaxChecksPerServer] = useState<number>(1);
  const [savingConcurrency, setSavingConcurrency] = useState(false);

  // Check distribution settings
  const [networkCheckPercent, setNetworkCheckPercent] = useState<number>(50);
  const [savingDistribution, setSavingDistribution] = useState(false);

  // Report settings
  const [reportIntervalMinutes, setReportIntervalMinutes] = useState<number>(60);
  const [maxReports, setMaxReports] = useState<number>(1000);
  const [savingReport, setSavingReport] = useState(false);

  // Stale-target classification (proxies/IPs continuously DOWN for ≥ N hours
  // are demoted to STALE and probed at 1/6 cadence). 0 disables the feature.
  const [staleThresholdHours, setStaleThresholdHours] = useState<number>(24);
  const [savingStale, setSavingStale] = useState(false);

  // Export state
  const [exportSections, setExportSections] = useState<string[]>(['settings', 'servers', 'webhooks']);
  const [exporting, setExporting] = useState(false);

  // Debug dump state
  const [dumpLoading, setDumpLoading] = useState(false);

  // Factory reset state
  const [resetting, setResetting] = useState(false);
  const [resetSections, setResetSections] = useState<string[]>([]);

  // Import state
  const [importData, setImportData] = useState<any>(null);
  const [importFileName, setImportFileName] = useState('');
  const [importSections, setImportSections] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/admin/settings');
      if (res.data?.success) {
        setCheckInterval(res.data.data.checkIntervalSeconds);
        setTicketsEnabled(res.data.data.ticketsEnabled ?? false);
        setProxyMaxLatencyMs(res.data.data.proxyMaxLatencyMs ?? 30000);
        setProxyTimeoutMs(res.data.data.proxyTimeoutMs ?? 30000);
        setCheckIntervalMobile(res.data.data.checkIntervalSecondsMobile ?? 180);
        setProxyMaxLatencyMsMobile(res.data.data.proxyMaxLatencyMsMobile ?? 3000);
        setProxyTimeoutMsMobile(res.data.data.proxyTimeoutMsMobile ?? 20000);
        if (res.data.data.proxyTargetConfigs?.length > 0) {
          setProxyTargetConfigs(res.data.data.proxyTargetConfigs);
        } else if (res.data.data.proxyTargetURLs?.length > 0) {
          // Migrate legacy URL-only list
          setProxyTargetConfigs(res.data.data.proxyTargetURLs.map((u: string) => ({ url: u, expectedContent: '' })));
        }
        setMaxConcurrentChecks(res.data.data.maxConcurrentChecks || 50);
        setMaxChecksPerServer(res.data.data.maxChecksPerServer || 1);
        setNetworkCheckPercent(res.data.data.networkCheckPercent ?? 50);
        setReportIntervalMinutes(res.data.data.reportIntervalMinutes ?? 60);
        setMaxReports(res.data.data.maxReports ?? 1000);
        setStaleThresholdHours(res.data.data.staleThresholdHours ?? 24);
      }
    } catch {
      // defaults are fine
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiClient.put('/admin/settings', { checkIntervalSeconds: checkInterval });
      if (res.data?.success) {
        message.success('Settings saved');
      }
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDistribution = async () => {
    setSavingDistribution(true);
    try {
      const res = await apiClient.put('/admin/settings', { networkCheckPercent });
      if (res.data?.success) {
        message.success('Check distribution saved');
      }
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to save check distribution');
    } finally {
      setSavingDistribution(false);
    }
  };

  const handleSaveConcurrency = async () => {
    setSavingConcurrency(true);
    try {
      const res = await apiClient.put('/admin/settings', { maxConcurrentChecks, maxChecksPerServer });
      if (res.data?.success) {
        message.success('Concurrency settings saved. Workers will adjust within 15 seconds.');
      }
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to save concurrency settings');
    } finally {
      setSavingConcurrency(false);
    }
  };

  const handleSaveStale = async () => {
    setSavingStale(true);
    try {
      const res = await apiClient.put('/admin/settings', { staleThresholdHours });
      if (res.data?.success) {
        message.success('Stale-target settings saved');
      }
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to save stale-target settings');
    } finally {
      setSavingStale(false);
    }
  };

  const handleSaveReport = async () => {
    setSavingReport(true);
    try {
      const res = await apiClient.put('/admin/settings', {
        reportIntervalMinutes,
        maxReports,
      });
      if (res.data?.success) {
        message.success('Report settings saved');
      }
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to save report settings');
    } finally {
      setSavingReport(false);
    }
  };

  const handleSaveMobile = async () => {
    setSavingMobile(true);
    try {
      const res = await apiClient.put('/admin/settings', {
        checkIntervalSecondsMobile: checkIntervalMobile,
        proxyMaxLatencyMsMobile: proxyMaxLatencyMsMobile,
        proxyTimeoutMsMobile: proxyTimeoutMsMobile,
      });
      if (res.data?.success) {
        message.success('Mobile-gateway settings saved');
      }
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to save mobile settings');
    } finally {
      setSavingMobile(false);
    }
  };

  const handleSaveProxy = async () => {
    setSavingProxy(true);
    try {
      const configs = proxyTargetConfigs
        .map((c) => ({ url: c.url.trim(), expectedContent: c.expectedContent?.trim() || '' }))
        .filter((c) => c.url);
      if (configs.length === 0) {
        message.error('At least one target URL is required');
        setSavingProxy(false);
        return;
      }
      const res = await apiClient.put('/admin/settings', {
        proxyMaxLatencyMs: proxyMaxLatencyMs,
        proxyTimeoutMs: proxyTimeoutMs,
        proxyTargetConfigs: configs,
      });
      if (res.data?.success) {
        message.success('Proxy checker settings saved');
      }
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to save proxy settings');
    } finally {
      setSavingProxy(false);
    }
  };

  const handleToggleTickets = async (enabled: boolean) => {
    if (!enabled) {
      // Turning off — ask about deleting existing tickets
      modal.confirm({
        title: 'Disable Ticket System',
        content: 'Do you want to delete all existing tickets? This cannot be undone.',
        okText: 'Disable & Delete Tickets',
        okType: 'danger',
        cancelText: 'Disable Only',
        onOk: async () => {
          setTogglingTickets(true);
          try {
            await apiClient.put('/admin/settings', { ticketsEnabled: false });
            const delRes = await apiClient.delete('/tickets');
            const deleted = delRes.data?.data?.deleted ?? 0;
            setTicketsEnabled(false);
            message.success(`Tickets disabled. ${deleted} ticket(s) deleted.`);
          } catch (err: any) {
            message.error(err.response?.data?.error || 'Failed to disable tickets');
          } finally {
            setTogglingTickets(false);
          }
        },
        onCancel: async () => {
          setTogglingTickets(true);
          try {
            await apiClient.put('/admin/settings', { ticketsEnabled: false });
            setTicketsEnabled(false);
            message.success('Tickets disabled. Existing tickets preserved.');
          } catch (err: any) {
            message.error(err.response?.data?.error || 'Failed to disable tickets');
          } finally {
            setTogglingTickets(false);
          }
        },
      });
    } else {
      // Turning on
      setTogglingTickets(true);
      try {
        await apiClient.put('/admin/settings', { ticketsEnabled: true });
        setTicketsEnabled(true);
        message.success('Ticket system enabled');
      } catch (err: any) {
        message.error(err.response?.data?.error || 'Failed to enable tickets');
      } finally {
        setTogglingTickets(false);
      }
    }
  };

  const handleDebugDump = async () => {
    setDumpLoading(true);
    try {
      const res = await apiClient.get('/debug/dump');
      if (res.data?.success) {
        const blob = new Blob([JSON.stringify(res.data.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `debug-dump-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
        a.click();
        URL.revokeObjectURL(url);
        message.success('Debug dump downloaded');
      }
    } catch {
      message.error('Failed to download debug dump');
    } finally {
      setDumpLoading(false);
    }
  };

  const handleExport = async () => {
    if (exportSections.length === 0) {
      message.warning('Select at least one section to export');
      return;
    }
    setExporting(true);
    try {
      const res = await apiClient.get(`/admin/export?include=${exportSections.join(',')}`);
      if (res.data?.success) {
        const blob = new Blob([JSON.stringify(res.data.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `proxy-network-diag-backup-${new Date().toISOString().slice(0, 10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
        message.success('Export downloaded');
      }
    } catch {
      message.error('Failed to export');
    } finally {
      setExporting(false);
    }
  };

  const handleFileRead = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);
        setImportData(data);
        setImportFileName(file.name);
        const sections: string[] = [];
        if (data.settings) sections.push('settings');
        if (data.servers && data.servers.length > 0) sections.push('servers');
        if (data.webhooks && data.webhooks.length > 0) sections.push('webhooks');
        setImportSections(sections);
      } catch {
        message.error('Invalid JSON file');
        setImportData(null);
      }
    };
    reader.readAsText(file);
    return false; // prevent upload
  };

  const handleImport = async () => {
    if (!importData || importSections.length === 0) {
      message.warning('Select at least one section to import');
      return;
    }
    setImporting(true);
    try {
      const payload: any = {};
      if (importSections.includes('settings') && importData.settings) {
        payload.settings = importData.settings;
      }
      if (importSections.includes('servers') && importData.servers) {
        payload.servers = importData.servers;
      }
      if (importSections.includes('webhooks') && importData.webhooks) {
        payload.webhooks = importData.webhooks;
      }
      const res = await apiClient.post('/admin/import', payload);
      if (res.data?.success) {
        const r = res.data.data;
        const parts: string[] = [];
        if (r.settingsImported) parts.push('Settings imported');
        if (r.serversCreated > 0) parts.push(`${r.serversCreated} servers created`);
        if (r.serversUpdated > 0) parts.push(`${r.serversUpdated} servers updated`);
        if (r.webhooksCreated > 0) parts.push(`${r.webhooksCreated} webhooks created`);
        if (r.errors?.length > 0) parts.push(`${r.errors.length} errors`);
        message.success(parts.join(', ') || 'Import complete');
        setImportData(null);
        setImportFileName('');
        setImportSections([]);
        loadSettings();
      }
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to import');
    } finally {
      setImporting(false);
    }
  };

  const clearImport = () => {
    setImportData(null);
    setImportFileName('');
    setImportSections([]);
  };

  const handleFactoryReset = () => {
    if (resetSections.length === 0) {
      message.warning('Select at least one section to reset');
      return;
    }

    const labelMap: Record<string, string> = {
      servers: 'Servers',
      tickets: 'Tickets',
      webhooks: 'Webhooks',
      checkLogs: 'Check Logs',
      reports: 'Reports',
      apiKeys: 'API Keys',
      settings: 'Settings',
      redis: 'Health Data (Redis)',
    };

    modal.confirm({
      title: 'Confirm Reset',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>The following data will be <strong>permanently deleted</strong>:</p>
          <ul style={{ paddingLeft: 20 }}>
            {resetSections.map((s) => (
              <li key={s}>{labelMap[s] || s}</li>
            ))}
          </ul>
          <p><strong>This action cannot be undone.</strong></p>
        </div>
      ),
      okText: 'Delete Selected',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setResetting(true);
        try {
          const res = await apiClient.post('/admin/reset', { sections: resetSections });
          if (res.data?.success) {
            const r = res.data.data;
            const parts: string[] = [];
            if (r.serversDeleted > 0) parts.push(`${r.serversDeleted} servers`);
            // Deleting servers takes their check data with it — say so, or the
            // operator has no way to tell whether the next report will still be
            // full of endpoints belonging to servers that are gone.
            if (r.redisKeysDeleted > 0) parts.push(`${r.redisKeysDeleted} check-data keys`);
            if (r.ticketsDeleted > 0) parts.push(`${r.ticketsDeleted} tickets`);
            if (r.webhooksDeleted > 0) parts.push(`${r.webhooksDeleted} webhooks`);
            if (r.checkLogsDeleted > 0) parts.push(`${r.checkLogsDeleted} check logs`);
            if (r.reportsDeleted > 0) parts.push(`${r.reportsDeleted} reports`);
            if (r.apiKeysDeleted > 0) parts.push(`${r.apiKeysDeleted} API keys`);
            if (r.settingsReset) parts.push('settings reset');
            if (r.redisFlush) parts.push('health data cleared');
            message.success(`Reset complete. ${parts.join(', ') || 'Data cleared'}`);
            setResetSections([]);
            loadSettings();
          }
        } catch (err: any) {
          message.error(err.response?.data?.error || 'Reset failed');
        } finally {
          setResetting(false);
        }
      },
    });
  };

  return {
    loading,
    // check interval
    checkInterval, setCheckInterval, handleSave, saving,
    // distribution
    networkCheckPercent, setNetworkCheckPercent, handleSaveDistribution, savingDistribution,
    // concurrency
    maxConcurrentChecks, setMaxConcurrentChecks, maxChecksPerServer, setMaxChecksPerServer,
    handleSaveConcurrency, savingConcurrency,
    // proxy checker
    proxyTargetConfigs, setProxyTargetConfigs, proxyTimeoutMs, setProxyTimeoutMs,
    proxyMaxLatencyMs, setProxyMaxLatencyMs, handleSaveProxy, savingProxy,
    // mobile gateway
    checkIntervalMobile, setCheckIntervalMobile, proxyMaxLatencyMsMobile, setProxyMaxLatencyMsMobile,
    proxyTimeoutMsMobile, setProxyTimeoutMsMobile, handleSaveMobile, savingMobile,
    // tickets
    ticketsEnabled, togglingTickets, handleToggleTickets,
    // reports
    reportIntervalMinutes, setReportIntervalMinutes, maxReports, setMaxReports,
    handleSaveReport, savingReport,
    // stale targets
    staleThresholdHours, setStaleThresholdHours, handleSaveStale, savingStale,
    // backup & restore
    exportSections, setExportSections, handleExport, exporting,
    importData, importFileName, importSections, setImportSections, importing,
    handleFileRead, handleImport, clearImport,
    // debug dump
    handleDebugDump, dumpLoading,
    // reset
    resetSections, setResetSections, handleFactoryReset, resetting,
  };
};

// What the cards are handed: the whole settings document plus its savers.
export type SettingsController = ReturnType<typeof useSettings>;

export default useSettings;
